// =============================================================================
// ImageUploader — pick/upload an image to Supabase Storage (the bucket that
// replaced the old Z: drive), preview it, replace it, or remove it. Returns the
// public URL via onChange so the parent can persist it (products.image_url /
// inventory_pictures.image_url).
// =============================================================================
import { useRef, useState } from 'react'
import { Upload, Trash2, Image as ImageIcon, Loader2 } from 'lucide-react'
import { uploadInventoryImage, deleteInventoryImageByUrl } from '../../services/data'
import { toast } from 'react-toastify'

export default function ImageUploader({ value, folder, onChange, label = 'Photo' }: {
  value: string | null
  folder: string
  onChange: (url: string | null) => void
  label?: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  const pick = async (file: File | undefined) => {
    if (!file) return
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image too large (max 10 MB)'); return }
    setBusy(true)
    try {
      const prev = value
      const url = await uploadInventoryImage(file, folder)
      onChange(url)
      if (prev) deleteInventoryImageByUrl(prev)  // best-effort cleanup of the replaced app upload
      toast.success('Photo uploaded')
    } catch (e) {
      console.error(e); toast.error('Upload failed — check your connection and try again')
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const remove = () => {
    const prev = value
    onChange(null)
    if (prev) deleteInventoryImageByUrl(prev)
  }

  return (
    <div>
      <label className="text-xs font-medium text-gray-500">{label}</label>
      <div className="mt-1 flex items-start gap-3">
        <div className="w-28 h-28 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
          {value
            ? <img src={value} alt="" className="object-cover w-full h-full" />
            : <ImageIcon size={28} className="text-gray-300" />}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={inputRef} type="file" accept="image/*" className="hidden"
            onChange={e => pick(e.target.files?.[0])} />
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            {busy ? 'Uploading…' : value ? 'Replace Photo' : 'Upload Photo'}
          </button>
          {value && !busy && (
            <button type="button" onClick={remove}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-red-600 hover:bg-red-50">
              <Trash2 size={15} /> Remove
            </button>
          )}
          <p className="text-xs text-gray-400 max-w-[12rem]">JPG or PNG, up to 10 MB. Stored securely in the cloud.</p>
        </div>
      </div>
    </div>
  )
}
